import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Radio, Trophy, Users, Clock, MapPin, Timer } from "lucide-react";

/**
 * /matches/:matchId — Individual Match Page with big scoreboard + live per-point ticker.
 * Subscribes to /api/public/events/stream for real-time updates. Renders the
 * match's format snapshot (set_based sets grid, or time-based totals).
 */
const STAGE_LABEL = {
  group: "Group Stage",
  quarter: "Quarterfinal",
  semi: "Semifinal",
  final: "Grand Final",
};
const fmtDate = (iso) => {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return isNaN(d) ? "TBD" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function MatchDetails() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState(false);
  const flashTimer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/matches/${matchId}`);
      setM(data);
      // brief flash to signal live update.
      setFlash(true);
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(false), 600);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Match not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matchId]);

  useEffect(() => {
    const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
    const es = new EventSource(`${base}/api/public/events/stream`);
    let pending = false;
    let cooldown = 0;
    const refresh = () => {
      const now = Date.now();
      if (now < cooldown) {
        // Coalesce bursts of point events into one refetch every 1.2s.
        if (!pending) {
          pending = true;
          setTimeout(() => {
            pending = false;
            cooldown = Date.now() + 1200;
            load();
          }, Math.max(200, cooldown - now));
        }
        return;
      }
      cooldown = now + 1200;
      load();
    };
    ["match.set_scored", "match.point_scored", "match.finalized", "match.started"].forEach((e) => es.addEventListener(e, refresh));
    es.onerror = () => { };
    return () => es.close();
    // eslint-disable-next-line
  }, [matchId]);

  if (loading) return <div className="p-10 text-zinc-400" data-testid="match-details-loading">Loading match…</div>;
  if (err) return <div className="p-10 text-red-400" data-testid="match-details-error">{err}</div>;
  if (!m) return null;

  const status = (m.status || "scheduled").toLowerCase();
  const isSet = m.format_type === "set_based";
  const gA = m.total_games_won_a ?? m.score_a ?? 0;
  const gB = m.total_games_won_b ?? m.score_b ?? 0;
  const winA = status === "finished" && gA > gB;
  const winB = status === "finished" && gB > gA;

  return (
    <div className="min-h-screen bg-[#060d09] text-white pb-16" data-testid={`match-details-${m.id}`}>
      {/* Hero */}
      <div className="relative border-b border-white/10 p-6 md:p-10 bg-gradient-to-b from-[#0a1611] to-[#060d09]">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white mb-4"
          data-testid="match-details-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex items-center gap-2 flex-wrap text-xs text-zinc-400 mb-2">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Mara Padel League · Match</span>
          <span>·</span>
          <span>{STAGE_LABEL[m.stage] || m.stage}</span>
          {m.seed && <span>· Seed {m.seed}</span>}
          {m.court && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.court}</span>}
          {m.scheduled_at && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(m.scheduled_at)}</span>}
        </div>
        <div className="flex items-center gap-2 mb-4">
          <StatusPill status={status} />
          {isSet && m.format_config && (
            <span className="text-[10px] uppercase text-zinc-500">
              {m.format_config.sets_per_match || 3}×{m.format_config.games_per_set || 6}{m.format_config.tiebreak ? " · tiebreak" : ""}
            </span>
          )}
          {!isSet && <span className="text-[10px] uppercase text-zinc-500">Time-Based</span>}
        </div>

        {/* Big scoreboard */}
        <div className={`grid grid-cols-[1fr_auto_1fr] gap-4 items-center py-6 transition-colors ${flash ? "bg-[#F4B400]/5" : ""}`} data-testid="match-scoreboard">
          <TeamSide team={m.team_a} big score={gA} highlight={winA} align="right" testId="scoreboard-team-a" />
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Games</div>
            <div className="text-zinc-500 text-4xl font-anton">—</div>
          </div>
          <TeamSide team={m.team_b} big score={gB} highlight={winB} align="left" testId="scoreboard-team-b" />
        </div>

        {/* Set grid (if set-based) */}
        {isSet && Array.isArray(m.set_scores) && m.set_scores.length > 0 && (
          <div className="mt-4 flex justify-center gap-2 flex-wrap" data-testid="match-set-grid">
            {m.set_scores.map((s, i) => (
              <div key={i} className="text-center px-4 py-2 rounded bg-black/40 border border-white/10 min-w-[70px]" data-testid={`match-set-${i + 1}`}>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">Set {i + 1}</div>
                <div className="font-anton text-2xl text-white mt-1">{s.a ?? 0}<span className="text-zinc-600 mx-1">·</span>{s.b ?? 0}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-6 md:p-10 grid gap-6 lg:grid-cols-3">
        {/* On-court lineups */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5 bg-[#0f1a15] border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#F4B400]" />
              <h2 className="font-anton text-xl uppercase tracking-tight">On Court</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <LineupCol team={m.team_a} playerIds={onCourt(m, "a")} lookup={m.player_lookup || {}} />
              <LineupCol team={m.team_b} playerIds={onCourt(m, "b")} lookup={m.player_lookup || {}} />
            </div>
          </Card>

          {/* Point-by-point ticker with highlights filter */}
          {Array.isArray(m.score_history) && m.score_history.length > 0 && (
            <Card className="p-5 bg-[#0f1a15] border-white/10" data-testid="match-ticker">
              <TimelineWithFilters history={m.score_history} teamA={m.team_a} teamB={m.team_b} setScores={m.set_scores || []} />
            </Card>
          )}
        </div>

        {/* Sidebar: sibling seeds + referee */}
        <div className="space-y-6">
          {Array.isArray(m.tie_siblings) && m.tie_siblings.length > 1 && (
            <Card className="p-5 bg-[#0f1a15] border-white/10" data-testid="match-tie-siblings">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-[#F4B400]" />
                <h2 className="font-anton text-xl uppercase tracking-tight">Tie · All Seeds</h2>
              </div>
              <div className="space-y-2">
                {m.tie_siblings.map((s) => (
                  <Link
                    key={s.id}
                    to={`/matches/${s.id}`}
                    className={`flex items-center gap-2 p-2 rounded border ${s.id === m.id ? "border-[#F4B400]/60 bg-[#F4B400]/5" : "border-white/10 hover:bg-black/30"}`}
                    data-testid={`sibling-seed-${s.seed}`}
                  >
                    <span className="text-[10px] uppercase text-[#F4B400] font-bold w-14">Seed {s.seed}</span>
                    <span className="text-[10px] uppercase text-zinc-500 flex-1 truncate">{s.court || "—"}</span>
                    <StatusPill status={s.status} small />
                    <span className="font-mono text-xs text-white min-w-[50px] text-right">
                      {s.total_games_won_a ?? s.score_a ?? 0}–{s.total_games_won_b ?? s.score_b ?? 0}
                    </span>
                  </Link>
                ))}
              </div>
            </Card>
          )}

          {m.referee?.name && (
            <Card className="p-5 bg-[#0f1a15] border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Referee</div>
              <div className="font-anton text-lg text-white mt-1">{m.referee.name}</div>
            </Card>
          )}

          <Card className="p-5 bg-[#0f1a15] border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Teams</div>
            <div className="space-y-2">
              {m.team_a && <Link to={`/teams/${m.team_a.id}`} className="flex items-center gap-2 hover:text-[#F4B400] text-sm" data-testid="link-team-a"><TeamAvatar team={m.team_a} /> {m.team_a.name}</Link>}
              {m.team_b && <Link to={`/teams/${m.team_b.id}`} className="flex items-center gap-2 hover:text-[#F4B400] text-sm" data-testid="link-team-b"><TeamAvatar team={m.team_b} /> {m.team_b.name}</Link>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function onCourt(m, side) {
  const active = (side === "a" ? m.active_players_a : m.active_players_b) || [];
  if (active.length) return active;
  // Fallback to lineup for this seed
  const seed = String(m.seed || "1");
  return ((side === "a" ? m.seed_lineup_a : m.seed_lineup_b) || {})[seed] || [];
}

function StatusPill({ status, small }) {
  const s = (status || "").toLowerCase();
  const map = {
    live: "bg-red-500/20 text-red-300 border-red-500/40 animate-pulse",
    scheduled: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
    finished: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  };
  return <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest border rounded px-1.5 ${small ? "py-0" : "py-0.5"} ${map[s] || map.scheduled}`}>
    {s === "live" && <Radio className="w-2.5 h-2.5" />} {s || "scheduled"}
  </span>;
}

function TeamAvatar({ team }) {
  return (
    <div className="w-6 h-6 rounded bg-black/40 overflow-hidden shrink-0">
      {team?.logo_url && <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />}
    </div>
  );
}

function TeamSide({ team, score, highlight, align, big, testId }) {
  if (!team) return <div />;
  return (
    <Link
      to={`/teams/${team.id}`}
      className={`flex items-center gap-3 group ${align === "right" ? "justify-end" : ""}`}
      data-testid={testId}
    >
      {align === "left" && <div className="w-16 h-16 rounded-lg bg-black/40 overflow-hidden shrink-0">{team.logo_url && <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />}</div>}
      <div className={align === "right" ? "text-right" : "text-left"}>
        <div className="font-anton text-2xl md:text-3xl uppercase tracking-tight group-hover:text-[#F4B400] transition-colors">{team.name}</div>
        <div className={`font-anton ${big ? "text-6xl md:text-8xl" : "text-5xl"} leading-none mt-2 ${highlight ? "text-emerald-400" : "text-white"}`}>{score}</div>
      </div>
      {align === "right" && <div className="w-16 h-16 rounded-lg bg-black/40 overflow-hidden shrink-0">{team.logo_url && <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />}</div>}
    </Link>
  );
}

function LineupCol({ team, playerIds, lookup }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 truncate">{team?.name}</div>
      <div className="space-y-1">
        {(playerIds || []).length === 0
          ? <div className="text-xs text-zinc-600 italic">No players on court</div>
          : (playerIds || []).map((pid) => {
              const p = lookup[pid];
              return (
                <Link key={pid} to={`/players/${pid}`} className="flex items-center gap-2 p-1.5 rounded bg-black/20 hover:bg-black/40 transition-colors" data-testid={`lineup-player-${pid}`}>
                  <div className="w-6 h-6 rounded-full bg-black/40 overflow-hidden">
                    {p?.photo_url && <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="text-xs text-white truncate">{p?.name || pid.slice(0, 6)}</div>
                </Link>
              );
            })}
      </div>
    </div>
  );
}

function PointRow({ pt, teamA, teamB, highlight, dense }) {
  const side = pt.team === "a" || pt.side === "a" ? "a" : "b";
  const name = side === "a" ? teamA?.name : teamB?.name;
  const color = side === "a" ? "text-emerald-300" : "text-sky-300";
  const a = pt.score_a_after ?? pt.a;
  const b = pt.score_b_after ?? pt.b;
  const value = pt.value ? `+${pt.value}` : "";
  const ts = pt.ts ? new Date(pt.ts).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
  return (
    <div className={`flex items-center gap-2 py-1 px-2 rounded ${highlight ? "bg-[#F4B400]/10 border border-[#F4B400]/40" : "bg-black/20 border border-white/5"} text-xs`}>
      <span className={`font-anton uppercase text-xs ${color} w-2/5 truncate`}>{name || side.toUpperCase()}</span>
      <span className="text-zinc-600 text-[10px]">{value}</span>
      <span className="font-mono text-white flex-1">{a ?? "0"}–{b ?? "0"}</span>
      {highlight && <span className="text-[9px] uppercase text-[#F4B400] font-bold">{highlight}</span>}
      {pt.set_index != null && <span className="text-[9px] uppercase text-zinc-500">Set {pt.set_index + 1}</span>}
      <span className="text-[9px] text-zinc-600 font-mono">{ts}</span>
    </div>
  );
}

/** TimelineWithFilters — derives highlight events (streaks, set-won) from the
 * raw point stream and lets the user filter to just the moments that matter. */
function TimelineWithFilters({ history, teamA, teamB, setScores }) {
  const [filter, setFilter] = React.useState("all"); // all | streaks | sets | side-a | side-b

  const annotated = React.useMemo(() => {
    const arr = history.map((p) => ({ ...p, _highlight: null }));
    // Compute streaks: 3+ consecutive points by same team.
    let last = null; let run = 0; let runStart = -1;
    for (let i = 0; i < arr.length; i++) {
      const side = arr[i].team === "a" || arr[i].side === "a" ? "a" : "b";
      if (side === last) run += 1;
      else { last = side; run = 1; runStart = i; }
      if (run === 3) {
        arr[runStart]._highlight = "3-Streak";
        arr[i]._highlight = "3-Streak";
      } else if (run >= 4) {
        arr[i]._highlight = `${run}-Streak`;
      }
    }
    // Mark set-won boundaries — the point where set_index increments.
    for (let i = 1; i < arr.length; i++) {
      const prev = arr[i - 1].set_index;
      const cur = arr[i].set_index;
      if (prev != null && cur != null && cur > prev) {
        arr[i - 1]._highlight = "Set Won";
      }
    }
    // Last-point-of-final-set → Match Point / Match Won
    if (arr.length > 0) {
      const lastRow = arr[arr.length - 1];
      if (!lastRow._highlight) lastRow._highlight = "Latest";
    }
    return arr;
  }, [history]);

  const filtered = React.useMemo(() => {
    let out = annotated;
    if (filter === "streaks") out = out.filter((p) => p._highlight && p._highlight !== "Latest" && p._highlight !== "Set Won");
    else if (filter === "sets") out = out.filter((p) => p._highlight === "Set Won");
    else if (filter === "side-a") out = out.filter((p) => (p.team || p.side) === "a");
    else if (filter === "side-b") out = out.filter((p) => (p.team || p.side) === "b");
    return [...out].reverse();
  }, [annotated, filter]);

  return (
    <>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Timer className="w-4 h-4 text-red-400" />
        <h2 className="font-anton text-xl uppercase tracking-tight">Timeline</h2>
        <span className="text-xs text-zinc-500">{history.length} points · {setScores.length} sets</span>
        <div className="ml-auto flex gap-1" data-testid="timeline-filters">
          {[
            ["all", "All"],
            ["streaks", "Streaks"],
            ["sets", "Sets Won"],
            ["side-a", teamA?.name || "Side A"],
            ["side-b", teamB?.name || "Side B"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`timeline-filter-${k}`}
              className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border truncate max-w-[110px] ${
                filter === k ? "bg-[#F4B400]/20 border-[#F4B400]/60 text-[#F4B400]" : "bg-black/30 border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 italic py-6 text-center">No entries match this filter yet.</div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto space-y-1 pr-1">
          {filtered.slice(0, 80).map((pt, idx) => (
            <PointRow key={`${idx}-${pt.ts || idx}`} pt={pt} teamA={teamA} teamB={teamB} highlight={pt._highlight === "Latest" ? null : pt._highlight} />
          ))}
        </div>
      )}
    </>
  );
}
